<template>
  <b-container class="home">
    <!--
    @drop.prevent="captureDrop"
    @dragenter.prevent="validateDrop"
    @dragover.prevent="validateDrop"
    -->
    <b-row>
      <b-col cols="12" lg="10" offset-lg="1">
        <h3>Define new Shotgrid entities</h3>
        <b-form-select
          class="my-1"
          v-model="category"
          :options="categoryOptions"
          :state="categoryIsValid"
        ></b-form-select>
        <b-file
          id="fileDrop"
          v-model="file"
          :state="fileIsValid"
          class="big-file-input"
          placeholder="Browse or drop excel file here..."
          drop-placeholder="Drop file here..."
          @input="processFileInput"
        ></b-file>
        <b-form-input
          v-model="sheetName"
          type="text"
          class="my-1"
          placeholder="Optional: Sheet name or index"
        ></b-form-input>
        <b-button class="mx-1 my-1" @click="validateFiles" variant="info" :disabled="!submitIsValid"
          >Validate file</b-button
        >
        <b-button class="mx-1 my-1" @click="submitFiles" variant="primary" :disabled="!submitIsValid"
          >Submit file</b-button
        >
      </b-col>
      <!-- TODO: Only show this block if we have a result object-->
      <b-col cols="12" lg="10" offset-lg="1">
        <div v-if="resultData.entities">
          <h4 class="mt-2">Report Summary</h4>
          <b-table small striped bordered :items="resultData.entities"></b-table>
        </div>
        <div v-if="resultData.details && resultData.details.length > 0">
          <h4 class="mt-2">Details & Errors</h4>
          <b-table small striped bordered :items="resultData.details"></b-table>
        </div>
      </b-col>
    </b-row>
    <b-modal
      ref="validationModal"
      title="Validating Excel"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <b-list-group>
        <b-list-group-item
          v-for="stage in validationInfo.stages"
          :key="stage.index"
          class="flex-column justify-content-between"
        >
          <b-icon v-if="stage.state == STAGE_STATES.WAITING" icon="arrow-clockwise"></b-icon>
          <b-icon
            v-if="stage.state == STAGE_STATES.IN_PROGRESS"
            icon="arrow-clockwise"
            variant="primary"
            animation="spin"
          ></b-icon>
          <b-icon v-if="stage.state == STAGE_STATES.FINISHED" icon="check-circle" variant="success"></b-icon>
          <b-icon v-if="stage.state == STAGE_STATES.ERROR" icon="x-circle" variant="danger"></b-icon>
          {{ stage.display_text }}
          <b-progress v-if="stage.progressTotal" :value="stage.progress" :max="stage.progressTotal" show-progress>
            <b-progress-bar :value="stage.progress">
              <span>
                <strong>{{ stage.progress.toFixed(1) }} %</strong>
              </span>
            </b-progress-bar>
          </b-progress>
        </b-list-group-item>
      </b-list-group>
      <div v-if="validationInfo.finished">
        <h3>Result</h3>
        <p>{{ validationInfo.summaryText }}</p>
        <!-- <ul>
          <li v-for="line in validationInfo.resultLines" :key="line">
            {{ line }}
          </li>
        </ul> -->
      </div>
      <b-button block @click="closeValidationModal" :disabled="!validationInfo.finished">Close</b-button>
    </b-modal>
    <b-modal
      ref="submitModal"
      title="Ingesting Excel"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <b-list-group>
        <b-list-group-item
          v-for="stage in submitInfo.stages"
          :key="stage.index"
          class="flex-column justify-content-between"
        >
          <b-icon v-if="stage.state == STAGE_STATES.WAITING" icon="arrow-clockwise"></b-icon>
          <b-icon
            v-if="stage.state == STAGE_STATES.IN_PROGRESS"
            icon="arrow-clockwise"
            variant="primary"
            animation="spin"
          ></b-icon>
          <b-icon v-if="stage.state == STAGE_STATES.FINISHED" icon="check-circle" variant="success"></b-icon>
          <b-icon v-if="stage.state == STAGE_STATES.ERROR" icon="x-circle" variant="danger"></b-icon>
          {{ stage.display_text }}
          <b-progress v-if="stage.progressTotal" :value="stage.progress" :max="stage.progressTotal" show-progress>
            <b-progress-bar :value="stage.progress">
              <span>
                <strong>{{ stage.progress.toFixed(1) }} %</strong>
              </span>
            </b-progress-bar>
          </b-progress>
        </b-list-group-item>
      </b-list-group>
      <div v-if="submitInfo.finished">
        <h3>Result</h3>
        <p>{{ submitInfo.resultText }}</p>
        <ul>
          <li v-for="line in submitInfo.resultLines" :key="line">{{ line }}</li>
        </ul>
      </div>
      <b-button block @click="closeSubmitModal" :disabled="!submitInfo.finished">Close</b-button>
    </b-modal>
  </b-container>
</template>

<style lang="scss">
// Custom SCSS rules from this GH issue (Bigger drop zone for file uploads):
// https://github.com/bootstrap-vue/bootstrap-vue/issues/4570#issuecomment-573729870
$file-height: 100px;

/* Might need adjusting depending on the chosen height */
$padding-percent: 0.4;

.big-file-input {
  height: $file-height;
}

.big-file-input > label {
  text-align: center;
  height: $file-height;

  /* Add a percent of height as padding-top for content */
  padding-top: $file-height * $padding-percent;
}

// This sets height and padding for the "Browse" button
.big-file-input > label::after {
  height: $file-height - 2px;
  padding-top: ($file-height - 2px) * $padding-percent;
}
</style>

<script>
import api from '@/backendapi'
import { defaultToastBody, defaultToastConfig } from '@/util'

const _STAGE_STATES = {
  WAITING: 10,
  IN_PROGRESS: 20,
  FINISHED: 30,
  ERROR: -100,
}

const _resetValidationInfo = () => {
  return {
    finished: false,
    responseObj: {},
    summaryText: '',
    stages: {
      UPLOAD: {
        index: 0,
        state: _STAGE_STATES.WAITING,
        display_text: 'Uploading file',
        progress: 0,
        progressTotal: 100,
      },
      WAIT_RESP: {
        index: 1,
        state: _STAGE_STATES.WAITING,
        display_text: 'Waiting for server validation',
        progress: 0,
        progressTotal: 100,
      },
    },
  }
}

const _resetSubmitInfo = () => {
  return {
    finished: false,
    responseObj: {},
    summaryText: '',
    stages: {
      UPLOAD: {
        index: 0,
        state: _STAGE_STATES.WAITING,
        display_text: 'Uploading file',
        progress: 0,
        progressTotal: 100,
      },
      WAIT_RESP: {
        index: 1,
        state: _STAGE_STATES.WAITING,
        display_text: 'Waiting for server processing',
        progress: 0,
        progressTotal: 100,
      },
    },
  }
}

export default {
  name: 'Ingest',
  data: function () {
    return {
      STAGE_STATES: _STAGE_STATES,
      file: null,
      sheetName: '',
      category: null,
      // categoryOptions: [
      //   { value: null, text: "Select a category", disabled: true },
      //   // TODO: hardcoded list
      //   { value: "mathematics", text: "Mathematics" },
      //   { value: "material", text: "Material" },
      //   { value: "vto", text: "VTO" },
      //   { value: "ocp", text: "OCP / Custom" },
      //   { value: "sgh", text: "SGH / Engraving" },
      // ],
      validationInfo: _resetValidationInfo(),
      submitInfo: _resetSubmitInfo(),
      resultData: {},
    }
  },
  computed: {
    fileIsDefined: function () {
      return this.file !== null
    },
    fileIsValid() {
      if (this.file == null) {
        return null
      }
      return this._validateFile(this.file)
    },
    categoryIsValid() {
      if (this.category == null) {
        return null
      }
      return this.category ? true : false
    },
    submitIsValid() {
      return this._validateFile(this.file) && this._validateCategory(this.category)
    },
    categoryOptions: function () {
      return this.$store.state.categoryOptions
    },
  },
  methods: {
    _validateCategory(category) {
      return category ? true : false // Truthy check
    },
    _validateFile(file) {
      if (!this.file) {
        return false
      }
      let fparts = file.name.split('.')
      if (fparts.length <= 1 || fparts[fparts.length - 1] !== 'xlsx') {
        return false
      }
      return true
    },
    captureDrop(e) {
      let droppedFiles = e.dataTransfer.files
      console.log('drop')
      e.preventDefault()
      e.stopPropagation()
      console.log(e)
      if (!droppedFiles)
        return // this tip, convert FileList to array, credit:
        //   https://www.smashingmagazine.com/2018/01/drag-drop-file-uploader-vanilla-js/
      ;[...droppedFiles].forEach((f) => {
        console.log(f)
      })
    },
    validateDrop(e) {
      console.log('validate')
      console.log(e)
    },
    processFileInput(file) {
      // TODO: Validation on file here?
      console.log(file)
    },
    _checkFiles() {
      if (!this.fileIsDefined) {
        let toastConfig = defaultToastConfig()
        toastConfig.title = 'No file set!'
        toastConfig.variant = 'warning'
        this.$bvToast.toast(`Please browse or drop a file on the file box.`, toastConfig)
        return false
      }
      return true
    },
    async _processFile(action, modalRef, info, resetInfo, pollTask, apiMethod) {
      // Preflight check
      if (!this._checkFiles()) {
        return
      }
      // Setup before showing modal
      this[info] = resetInfo()
      this.resultData = {}
      this[info].stages.UPLOAD.state = _STAGE_STATES.IN_PROGRESS
      this[info].stages.WAIT_RESP.state = _STAGE_STATES.IN_PROGRESS
      this.$store.commit('setLeaveGuard', true)
      this.$refs[modalRef].show()
      // Step 1: Upload file
      let fileData = new FormData()
      let _file = this.file
      fileData.append('file', _file, _file.name)
      let requestConfig = {
        onUploadProgress: (progEvt) => {
          this[info].stages.UPLOAD.progress = (progEvt.loaded / progEvt.total) * 100
          if (this[info].stages.UPLOAD.progress >= 99.999999) {
            this[info].stages.UPLOAD.state = _STAGE_STATES.FINISHED
          }
        },
      }
      try {
        let res = await apiMethod(localStorage.token, this.category, this.sheetName, fileData, requestConfig)
        if (res.status === 202 && res.data.taskId) {
          console.log(`Moving on with polling, ${action} response:`)
          console.log(res)
          pollTask(res.data.taskId)
        } else {
          console.log(`${action} task not started, response:`)
          console.log(res)
          this._setResult(action, res)
          this[info].stages.WAIT_RESP.state = res.status === 200 ? _STAGE_STATES.FINISHED : _STAGE_STATES.ERROR
          this[info].finished = true
        }
      } catch (err) {
        console.log(err)
        this[info].stages.WAIT_RESP.state = _STAGE_STATES.ERROR
        let resp = err.response
        this.$bvToast.toast(defaultToastBody(resp), defaultToastConfig(resp))
      } finally {
        this.$store.commit('setLeaveGuard', false)
      }
    },
    async validateFiles() {
      await this._processFile(
        'validate',
        'validationModal',
        'validationInfo',
        _resetValidationInfo,
        this._pollValidationTask,
        api.postValidateExcel
      )
    },
    async submitFiles() {
      await this._processFile(
        'submit',
        'submitModal',
        'submitInfo',
        _resetSubmitInfo,
        this._pollSubmitTask,
        api.postSubmitExcel
      )
    },
    _setResult(action, resp) {
      if (action === 'validate') {
        this._showValidationResult(resp)
      } else if (action === 'submit') {
        this._setSubmitResult(resp)
      }
    },
    async _pollValidationTask(taskId) {
      try {
        let pollInterval = setInterval(async () => {
          console.log('polling task status')
          let res = await api.getJobStatus(localStorage.token, taskId)
          console.log('poll response:')
          console.log(res)
          if (res.data.status === 'Running') {
            this.validationInfo.stages.WAIT_RESP.progress = (res.data.completed_rows / res.data.total_rows) * 100
          } else if (res.data.status === 'Completed') {
            clearInterval(pollInterval)
            this.resultData = res.data.result
            this._showValidationResult({ data: res.data.result })
            this.validationInfo.stages.WAIT_RESP.progress=100
            this.validationInfo.stages.WAIT_RESP.state = _STAGE_STATES.FINISHED
            this.validationInfo.finished = true

          } else if (res.data.status === 'Failed') {
            clearInterval(pollInterval)
            this.validationInfo.stages.WAIT_RESP.state = _STAGE_STATES.ERROR
            this.validationInfo.finished = true
            this.$bvToast.toast('Validation task failed.', defaultToastConfig({ variant: 'danger' }))
          }
        }, 5000)
      } catch (err) {
        console.log(err)
        this.validationInfo.stages.WAIT_RESP.state = _STAGE_STATES.ERROR
        this.validationInfo.finished = true
        this.$bvToast.toast('Error polling validation task.', defaultToastConfig({ variant: 'danger' }))
      }
    },
    _showValidationResult(resp) {
      let respData = resp.data
      /** Populate validation messages and show the UI elements. */
      console.log('validation response:')
      console.log(respData)
      let rowsSummary = respData.summary.rows
      this.validationInfo.summaryText = `Successfully processed ${rowsSummary.success_count} out of ${rowsSummary.total_rows} rows. 
      Skipped ${rowsSummary.skip_count} due to errors. See the detail report below.`
      // Populate result data
      let entityData = []
      for (const [key, value] of Object.entries(respData.entities)) {
        entityData.push({
          "Entity": key,
          "Created": value.created,
          "Updated": value.updated
        })
      }
      this.resultData = {
        entities: entityData,
        details: respData.details
      }
    },
    closeValidationModal() {
      this.$refs['validationModal'].hide()
    },
    async _pollSubmitTask(taskId) {
      try {
        let pollInterval = setInterval(async () => {
          console.log('polling task status')
          let res = await api.getJobStatus(localStorage.token, taskId)
          console.log('poll response:')
          console.log(res)
          if (res.data.status === 'Running') {
            this.submitInfo.stages.WAIT_RESP.progress = (res.data.completed_rows / res.data.total_rows) * 100
          } else if (res.data.status === 'Completed') {
            clearInterval(pollInterval)
            this._setSubmitResult({ data: res.data.result })
            this.submitInfo.stages.WAIT_RESP.state = _STAGE_STATES.FINISHED
            this.submitInfo.stages.WAIT_RESP.progress = 100
            this.submitInfo.finished = true
          } else if (res.data.status === 'Failed') {
            clearInterval(pollInterval)
            this.submitInfo.stages.WAIT_RESP.state = _STAGE_STATES.ERROR
            this.submitInfo.finished = true
            this.$bvToast.toast('Submit task failed.', defaultToastConfig({ variant: 'danger' }))
          }
        }, 2000)
      } catch (err) {
        console.log(err)
        this.submitInfo.stages.WAIT_RESP.state = _STAGE_STATES.ERROR
        this.submitInfo.finished = true
        this.$bvToast.toast('Error polling submit task.', defaultToastConfig({ variant: 'danger' }))
      }
    },
    _setSubmitResult(resp) {
      let respData = resp.data
      console.log('submit response data:')
      console.log(respData)
      let rowsSummary = respData.summary.rows
      this.submitInfo.summaryText = `Successfully processed ${rowsSummary.success_count} out of ${rowsSummary.total_rows} rows. 
      Skipped ${rowsSummary.skip_count} due to errors. See the detail report below.`
      // Populate result data
      let entityData = []
      for (const [key, value] of Object.entries(respData.entities)) {
        entityData.push({
          "Entity": key,
          "Created": value.created,
          "Updated": value.updated
        })
      }
      this.resultData = {
        entities: entityData,
        details: respData.details
      }
    },
    closeSubmitModal() {
      this.$refs['submitModal'].hide()
    },
  },
  components: {},
  // beforeRouteLeave(to, from, next) {
  //   console.log('LEAVE GUARD CHECK')
  //   if (!this.leaveGuard) {
  //     next();
  //     return;
  //   }
  //   const answer = window.confirm(
  //     "Leaving this page will stop the current upload, are you sure?"
  //   );
  //   if (answer) {
  //     next();
  //   } else {
  //     next(false);
  //   }
  // },
}
</script>
